/**
 * Coco (Cotera-included) tool constants and helpers.
 * Source of truth lives in @cotera/api.
 */
import { coco as apiCoco } from '@cotera/api';

const {
  agentIdToAgentToolKey,
  COCO_AGENT_EDITOR_TOOL_KEY,
  COCO_AGENT_TOOL_KEY_PREFIX,
  COCO_ARCHIVE_RESOURCE_TOOL_KEY,
  COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  COCO_BUILTIN_TOOL_KEYS,
  COCO_BUILTIN_TOOL_KEYS_SET,
  COCO_COTERA_WORKSPACE_TOOL_KEY,
  COCO_CREATE_AGENT_TOOL_KEY,
  COCO_CREATE_DOCUMENT_TOOL_KEY,
  COCO_DATASET_READ_TOOL_KEY,
  COCO_DEFAULT_LOADED_TOOL_KEYS,
  COCO_DOCUMENT_EDITOR_TOOL_KEY,
  COCO_EDIT_AGENT_TOOL_KEY,
  COCO_EDIT_DOCUMENT_TOOL_KEY,
  COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY,
  COCO_FOLDER_CREATE_TOOL_KEY,
  COCO_FOLDER_EDITOR_TOOL_KEY,
  COCO_FOLDER_MOVE_RESOURCE_TOOL_KEY,
  COCO_FOLDER_RENAME_TOOL_KEY,
  COCO_FOR_EACH_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_ORGANIZE_LIBRARY_TOOL_KEY,
  COCO_ORGANIZE_READ_DRAFT_TOOL_KEY,
  COCO_ORGANIZE_STAGE_MOVE_TOOL_KEY,
  COCO_ORGANIZE_ADD_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_REMOVE_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_DRAFT_TOOL_KEYS,
  COCO_READ_DOCUMENT_AS_MARKDOWN_TOOL_KEY,
  COCO_READ_DOCUMENT_TOOL_KEY,
  COCO_SEARCH_EXCLUDED_TOOL_KEYS,
  COCO_SEARCH_TOOL_KEY,
  COCO_SELECT_FOLDER_TOOL_KEY,
  COCO_SITE_SCRAPER_TOOL_KEY,
  COCO_WEB_SEARCH_TOOL_KEY,
  COCO_TOOL_KEY_PREFIX,
  COCO_USER_OPTIONS_TOOL_KEY,
  COCO_SLEEP_TOOL_KEY,
  COCO_RELAY_TOOL_KEY,
  DEFAULT_COCO_TOOL_KEYS,
  isAgentToolKey,
  isCocoToolKey,
  isWorkflowToolKey,
  toolKeyForMetrics,
  workflowIdFromToolKey,
  workflowIdToWorkflowToolKey,
  COCO_INGOT_QUERY_TOOL_KEY,
  COCO_UPLOAD_FILE_TOOL_KEY,
  COCO_COTERA_AGENT_TOOL_KEY,
  COCO_COTERA_WORKFLOW_TOOL_KEY,
  SUB_AGENT_EXCLUDED_TOOL_KEYS,
} = apiCoco;

export {
  agentIdToAgentToolKey,
  COCO_AGENT_EDITOR_TOOL_KEY,
  COCO_AGENT_TOOL_KEY_PREFIX,
  COCO_ARCHIVE_RESOURCE_TOOL_KEY,
  COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  COCO_BUILTIN_TOOL_KEYS,
  COCO_BUILTIN_TOOL_KEYS_SET,
  COCO_COTERA_AGENT_TOOL_KEY,
  COCO_COTERA_WORKFLOW_TOOL_KEY,
  COCO_COTERA_WORKSPACE_TOOL_KEY,
  COCO_CREATE_AGENT_TOOL_KEY,
  COCO_CREATE_DOCUMENT_TOOL_KEY,
  COCO_DATASET_READ_TOOL_KEY,
  COCO_DEFAULT_LOADED_TOOL_KEYS,
  COCO_DOCUMENT_EDITOR_TOOL_KEY,
  COCO_EDIT_AGENT_TOOL_KEY,
  COCO_EDIT_DOCUMENT_TOOL_KEY,
  COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY,
  COCO_FOLDER_CREATE_TOOL_KEY,
  COCO_FOLDER_EDITOR_TOOL_KEY,
  COCO_FOLDER_MOVE_RESOURCE_TOOL_KEY,
  COCO_FOLDER_RENAME_TOOL_KEY,
  COCO_FOR_EACH_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_ORGANIZE_LIBRARY_TOOL_KEY,
  COCO_ORGANIZE_READ_DRAFT_TOOL_KEY,
  COCO_ORGANIZE_STAGE_MOVE_TOOL_KEY,
  COCO_ORGANIZE_ADD_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_REMOVE_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_DRAFT_TOOL_KEYS,
  COCO_READ_DOCUMENT_AS_MARKDOWN_TOOL_KEY,
  COCO_READ_DOCUMENT_TOOL_KEY,
  COCO_SEARCH_EXCLUDED_TOOL_KEYS,
  COCO_SEARCH_TOOL_KEY,
  COCO_SELECT_FOLDER_TOOL_KEY,
  COCO_SITE_SCRAPER_TOOL_KEY,
  COCO_WEB_SEARCH_TOOL_KEY,
  COCO_TOOL_KEY_PREFIX,
  COCO_USER_OPTIONS_TOOL_KEY,
  COCO_SLEEP_TOOL_KEY,
  COCO_RELAY_TOOL_KEY,
  DEFAULT_COCO_TOOL_KEYS,
  isAgentToolKey,
  isCocoToolKey,
  isWorkflowToolKey,
  toolKeyForMetrics,
  workflowIdFromToolKey,
  workflowIdToWorkflowToolKey,
  COCO_INGOT_QUERY_TOOL_KEY,
  COCO_UPLOAD_FILE_TOOL_KEY,
  SUB_AGENT_EXCLUDED_TOOL_KEYS,
};

export type CocoBuiltinToolKey = (typeof COCO_BUILTIN_TOOL_KEYS)[number];

/** Coco tool constants and helpers. Use coco.isCocoToolKey(key) or coco.isAgentToolKey(key). */
export const coco = {
  prefix: COCO_TOOL_KEY_PREFIX,
  builtinKeys: COCO_BUILTIN_TOOL_KEYS,
  builtinKeysSet: COCO_BUILTIN_TOOL_KEYS_SET,
  defaultToolKeys: DEFAULT_COCO_TOOL_KEYS,
  COCO_SEARCH_TOOL_KEY,
  COCO_WEB_SEARCH_TOOL_KEY,
  COCO_SITE_SCRAPER_TOOL_KEY,
  COCO_USER_OPTIONS_TOOL_KEY,
  COCO_SELECT_FOLDER_TOOL_KEY,
  COCO_INGOT_QUERY_TOOL_KEY,
  COCO_UPLOAD_FILE_TOOL_KEY,
  COCO_CREATE_AGENT_TOOL_KEY,
  COCO_EDIT_AGENT_TOOL_KEY,
  COCO_READ_DOCUMENT_AS_MARKDOWN_TOOL_KEY,
  COCO_READ_DOCUMENT_TOOL_KEY,
  COCO_CREATE_DOCUMENT_TOOL_KEY,
  COCO_EDIT_DOCUMENT_TOOL_KEY,
  COCO_EDIT_STRUCTURED_OUTPUT_TOOL_KEY,
  COCO_DOCUMENT_EDITOR_TOOL_KEY,
  COCO_DATASET_READ_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  COCO_FOR_EACH_TOOL_KEY,
  COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  COCO_AGENT_EDITOR_TOOL_KEY,
  COCO_COTERA_WORKSPACE_TOOL_KEY,
  COCO_FOLDER_EDITOR_TOOL_KEY,
  COCO_FOLDER_CREATE_TOOL_KEY,
  COCO_FOLDER_RENAME_TOOL_KEY,
  COCO_FOLDER_MOVE_RESOURCE_TOOL_KEY,
  COCO_ORGANIZE_LIBRARY_TOOL_KEY,
  COCO_ORGANIZE_READ_DRAFT_TOOL_KEY,
  COCO_ORGANIZE_STAGE_MOVE_TOOL_KEY,
  COCO_ORGANIZE_ADD_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_REMOVE_FOLDER_TOOL_KEY,
  COCO_ORGANIZE_DRAFT_TOOL_KEYS,
  COCO_ARCHIVE_RESOURCE_TOOL_KEY,
  COCO_SLEEP_TOOL_KEY,
  COCO_RELAY_TOOL_KEY,
  COCO_SEARCH_EXCLUDED_TOOL_KEYS,
  isCocoToolKey,
  isAgentToolKey,
  isWorkflowToolKey,
  toolKeyForMetrics,
  workflowIdFromToolKey,
  workflowIdToWorkflowToolKey,
  agentIdToAgentToolKey,
  COCO_COTERA_AGENT_TOOL_KEY,
  COCO_COTERA_WORKFLOW_TOOL_KEY,
} as const;
